import React, { useRef, useState, useEffect, useCallback } from "react";
import "./Skills.css";

const sortByProficiency = (skills) =>
  [...skills].sort((a, b) => b.proficiency - a.proficiency);

const SkillCategory = ({ title, iconClass, skills }) => {
  const gridRef = useRef(null);
  const boxRef = useRef(null);
  const hasSorted = useRef(false);
  const [displaySkills, setDisplaySkills] = useState(skills);
  const [animateBars, setAnimateBars] = useState(false);

  useEffect(() => {
    hasSorted.current = false;
    setDisplaySkills(skills);
    setAnimateBars(false);
  }, [skills]);

  const runSortAnimation = useCallback(() => {
    if (hasSorted.current) return;
    hasSorted.current = true;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const sorted = sortByProficiency(skills);
    const grid = gridRef.current;

    if (!grid || prefersReducedMotion) {
      setDisplaySkills(sorted);
      setAnimateBars(true);
      return;
    }

    const firstRects = new Map();
    Array.from(grid.children).forEach((el) => {
      firstRects.set(el.dataset.skillName, el.getBoundingClientRect());
    });

    setDisplaySkills(sorted);
    setAnimateBars(true);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        Array.from(grid.children).forEach((el) => {
          const first = firstRects.get(el.dataset.skillName);
          if (!first) return;

          const last = el.getBoundingClientRect();
          const dx = first.left - last.left;
          const dy = first.top - last.top;

          if (dx !== 0 || dy !== 0) {
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            el.style.transition = "transform 0s";

            requestAnimationFrame(() => {
              el.style.transition = "transform 0.6s ease";
              el.style.transform = "";
            });
          }
        });
      });
    });
  }, [skills]);

  useEffect(() => {
    const node = boxRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          runSortAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [runSortAnimation]);

  return (
    <div className="skills-box" ref={boxRef}>
      <h4 className="skills-header">
        <i className={iconClass}></i>
        {title}
      </h4>
      <div className="skills-grid" ref={gridRef}>
        {displaySkills.map((skill) => (
          <div
            key={skill.name}
            className="skill-item"
            data-skill-name={skill.name}
          >
            <span className="skill-name">{skill.name}</span>
            <div className="skill-level">
              <div
                className="skill-progress"
                style={{ width: animateBars ? `${skill.proficiency}%` : "0%" }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Skills = ({ sectionHeaders = {}, skills = {} }) => {
  const categories = [
    {
      title: sectionHeaders.programmingLanguages || "Programming Languages",
      iconClass: "fas fa-code",
      skills: skills.programmingLanguages || [],
    },
    {
      title: sectionHeaders.frameworksLibraries || "Frameworks & Libraries",
      iconClass: "fas fa-layer-group",
      skills: skills.frameworksLibraries || [],
    },
    {
      title: sectionHeaders.toolsTechnologies || "Tools & Technologies",
      iconClass: "fas fa-wrench",
      skills: skills.toolsTechnologies || [],
    },
  ];

  return (
    <section className="skills-section">
      <h3 className="section-header">
        <i className="fas fa-tools"></i>
        Technical Skills
      </h3>
      <div className="skills-container">
        {categories.map((category) => (
          <SkillCategory key={category.title} {...category} />
        ))}
      </div>
    </section>
  );
};

export default Skills;
